import React, { useEffect, useState, useMemo } from 'react'
import { MapContainer, Marker, Popup, TileLayer, useMapEvents } from 'react-leaflet'
import L from 'leaflet'
import { Box, Button, Card, CardContent, CardHeader, Chip, MenuItem, Skeleton, TextField, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Globe, AlertCircle, Maximize, Minimize, X, Map as MapIcon } from 'lucide-react'
import { useToast } from "@crm-src/hooks/use-toast"
import { useNavigate } from "react-router-dom";
import type { Customer } from "@crm-src/types/customer"
import { CustomerWorldGlobe } from "@crm-src/components/customer-world-globe"
import { generateColorPalette } from "@crm-src/components/customer-world-map/colors"
import { spreadOverlappingPins, precisionNote } from "@crm-src/components/customer-world-map/pin-spread"
import { logoMarkerHtml, LOGO_MARKER_SIZE } from "@crm-src/components/customer-world-map/logo-marker"
import { crmApiUrl } from "@crm-src/lib/api"
import { BackButton } from "@/components/ui/back-button"

// Fix Leaflet marker icons in Next.js
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

/**
 * A customer drawn as their own logo: a coloured ring around a white disc.
 *
 * The ring carries exactly what the old teardrop carried (customer vs
 * prospect, or the product/feature palette), so switching to logos costs no
 * information. When there is no logo the disc is empty and the marker reads as
 * the coloured dot it always was — which is also what happens if the image
 * fails to load, because the `<img>` removes itself. See `logo-marker.ts`.
 */
function createLogoIcon(logoSrc: string | null, color: string, label?: string | null): L.DivIcon {
  return L.divIcon({
    className: "xop-customer-logo-marker",
    html: logoMarkerHtml({ logoSrc, color, label }),
    iconSize: [LOGO_MARKER_SIZE, LOGO_MARKER_SIZE],
    iconAnchor: [LOGO_MARKER_SIZE / 2, LOGO_MARKER_SIZE / 2],
    popupAnchor: [0, -LOGO_MARKER_SIZE / 2],
  })
}

/**
 * Reports the map's current zoom upward.
 *
 * Co-located pins are fanned apart by a fixed number of SCREEN pixels, which
 * means the component has to know the zoom to convert that into degrees. A
 * Leaflet map does not expose its zoom to a parent, so this null-rendering
 * child listens for it from the inside.
 */
function ZoomReporter({ onZoom }: { onZoom: (zoom: number) => void }) {
  const map = useMapEvents({
    zoomend: () => onZoom(map.getZoom()),
  })
  return null
}

// Vite is client-only; no SSR concerns. Statically importing leaflet
// components is fine and lets esbuild tree-shake them properly.

interface CustomerWithCoordinates extends Customer {
  latitude?: number
  longitude?: number
  geocoded?: boolean
  geocoding_error?: string
}

// Filter interfaces  
interface ProductHierarchy {
  products: Array<{
    id: number
    name: string
    code?: string
  }>
  product_features: Record<number, Array<{
    id: number
    name: string
    code?: string
  }>>
}

interface WorldMapFilters {
  customerType: 'all' | 'leads' | 'customers'
  productId: number | null
  productFeatureId: number | null
}

interface CustomerWorldMapProps {
  customers: Customer[]
  allCustomers?: Customer[] // Unfiltered customers for calculating counts
  loading?: boolean
  onCustomerClick?: (customer: Customer) => void
  hasProductFilter?: boolean
  // Filter-related props
  productHierarchy?: ProductHierarchy
  filters?: WorldMapFilters
  onFilterChange?: (filterType: keyof WorldMapFilters, value: any) => void
  onRemoveFilter?: (filterType: keyof WorldMapFilters) => void
  onResetFilters?: () => void
  getActiveFilterCount?: () => number
}

/**
 * The small print under a marker: how much this pin is worth, what the
 * geocoder actually found, and whether it had to be nudged to stay visible.
 *
 * The `hq_display_name` line is the important one. It is the EVIDENCE — the
 * name of the place crm-api resolved, verbatim ("Greenwich Town Hall, 101,
 * Field Point Road"). The 2026-08-31 mis-pins put North Dakota customers in
 * Austria and went unnoticed for months precisely because a marker showed no
 * trace of what it was claiming; anybody who had opened one would have seen
 * "Neusiedl am See" and known instantly.
 */
function PinProvenance({ customer }: { customer: CustomerWithCoordinates & { sharedWith?: number } }) {
  const note = precisionNote((customer as any).coordinate_precision)
  const found = (customer as any).hq_display_name as string | null | undefined
  const shared = customer.sharedWith ?? 1
  if (!note && !found && shared <= 1) return null

  return (
    <Box sx={{ fontSize: "0.7rem", color: "text.disabled", mt: 1, lineHeight: 1.4 }}>
      {found && (
        <Box component="span" sx={{ display: "block" }}>
          Located at: {found}
        </Box>
      )}
      {note}
      {shared > 1 && (
        <Box component="span" sx={{ display: "block" }}>
          {shared} customers share this location — markers are spread apart to stay visible.
        </Box>
      )}
    </Box>
  )
}

export function CustomerWorldMap({ 
  customers, 
  allCustomers,
  loading = false, 
  onCustomerClick, 
  hasProductFilter = false,
  productHierarchy,
  filters,
  onFilterChange,
  onRemoveFilter,
  onResetFilters,
  getActiveFilterCount
}: CustomerWorldMapProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [mapCustomers, setMapCustomers] = useState<CustomerWithCoordinates[]>([])
  const [mapLoaded, setMapLoaded] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [colorPalette, setColorPalette] = useState<Record<string, string>>({})
  const [mapMode, setMapMode] = useState<'flat' | 'globe'>('flat')
  // Drives the pin-spread ring: a cluster separates as the operator zooms in
  // and closes back up as they zoom out, staying ~14px apart throughout.
  const [zoomLevel, setZoomLevel] = useState(2)

  // Debug logging
  
  // Extract unique values for coloring options
  const uniqueProducts = useMemo(() => {
    const products = customers
      .filter(c => (c as any).product?.name)
      .map(c => (c as any).product.name)
    return Array.from(new Set(products)).sort()
  }, [customers])

  const uniqueFeatures = useMemo(() => {
    const features = customers
      .filter(c => (c as any).product_feature?.name)
      .map(c => (c as any).product_feature.name)
    return Array.from(new Set(features)).sort()
  }, [customers])

  // Simple coloring logic: Customer/Prospect by default, Features/Versions when product is selected
  const coloringMode = useMemo(() => {
    if (!hasProductFilter) {
      return 'type' // Always show Customer vs Prospect when no product filter
    }
    
    // When product is filtered, show the most specific level available.
    // Features are that level since 2026-09-13 — there is nothing below them.
    if (uniqueFeatures.length > 1) return 'feature'
    return 'type' // Fallback to type if no features/versions
  }, [hasProductFilter, uniqueFeatures.length])

  // Generate color palette based on determined coloring mode
  useEffect(() => {
    let items: string[] = []
    
    switch (coloringMode) {
      case 'type':
        items = ['Customer', 'Prospect']
        setColorPalette({ 'Customer': '#FF6B6B', 'Prospect': '#4285F4' }) // Match actual marker colors: red for customer, blue for prospect
        break
      case 'feature':
        items = uniqueFeatures
        setColorPalette(generateColorPalette(items))
        break
    }
  }, [coloringMode, uniqueProducts, uniqueFeatures])

  // The colour a customer is drawn in — the marker ring here, the dot on the
  // 3D globe canvas.
  const getMarkerColor = (customer: CustomerWithCoordinates): string => {
    switch (coloringMode) {
      case 'type':
        return (customer as any).type?.toLowerCase() === 'lead' ? '#3498DB' : '#E74C3C'
      case 'feature': {
        const key = (customer as any).product_feature?.name || ''
        return colorPalette[key] || '#999999'
      }
      default:
        return '#E74C3C'
    }
  }

  // The marker for a customer: their logo inside a ring in that colour.
  //
  // `logo_url` is a RELATIVE crm-api path, so it goes through `crmApiUrl` —
  // the bundled desktop shell has no proxy at its `tauri://` origin and a bare
  // relative src there resolves to nothing (docs/desktop-app.md).
  const getMarkerIcon = (customer: CustomerWithCoordinates): L.Icon | L.DivIcon => {
    const logoPath = (customer as any).logo_url as string | null | undefined
    const logoSrc = logoPath ? crmApiUrl(logoPath) : null
    return createLogoIcon(logoSrc, getMarkerColor(customer), customer.company_name)
  }

  // Get the display value for a customer based on coloring mode
  const getCustomerDisplayValue = (customer: CustomerWithCoordinates) => {
    switch (coloringMode) {
      case 'type':
        return (customer as any).type?.toLowerCase() === 'lead' ? 'Prospect' : 'Customer'
      case 'feature':
        return (customer as any).product_feature?.name || 'No Feature'
      default:
        return 'Unknown'
    }
  }


  // Process customers when they change (no longer need to geocode)
  useEffect(() => {
    
    if (customers.length === 0) {
      // Clear map when no customers match filters
      setMapCustomers([])
      return
    }
    
    const processedCustomers: CustomerWithCoordinates[] = customers.map(customer => {
      const detailedCustomer = customer as any
      
      if (detailedCustomer.latitude && detailedCustomer.longitude) {
        return {
          ...customer,
          latitude: parseFloat(detailedCustomer.latitude),
          longitude: parseFloat(detailedCustomer.longitude),
          geocoded: true
        }
      } else {
        return {
          ...customer,
          geocoded: false,
          geocoding_error: 'No coordinates available - address may need to be updated'
        }
      }
    })
    
    setMapCustomers(processedCustomers)
  }, [customers])

  // Get customers that have valid coordinates.
  //
  // `spreadOverlappingPins` fans out anything sharing one coordinate. Since the
  // CRM started falling back to city/state/country centroids (so that no
  // customer is invisible), a stack of ten customers on one centroid would
  // otherwise draw as a single marker — which reads as nine missing customers.
  // The displacement is metres-to-tens-of-km and presentation-only; each pin
  // keeps its stored coordinate as `trueLatitude`/`trueLongitude`.
  const mappableCustomers = useMemo(() => {
    const filtered = mapCustomers.filter(customer => customer.geocoded && customer.latitude && customer.longitude)
    return spreadOverlappingPins(filtered, zoomLevel)
  }, [mapCustomers, zoomLevel])

  // Calculate stats for display with color breakdown
  const stats = useMemo(() => {
    const totalCustomers = customers.length
    const mappableCount = mappableCustomers.length
    const unmappableCount = totalCustomers - mappableCount
    const successRate = totalCustomers > 0 ? (mappableCount / totalCustomers * 100).toFixed(1) : '0'
    
    // Calculate breakdown by current coloring mode
    const breakdown: Record<string, number> = {}
    mappableCustomers.forEach(customer => {
      const key = getCustomerDisplayValue(customer)
      breakdown[key] = (breakdown[key] || 0) + 1
    })
    
    return {
      totalCustomers,
      mappableCount,
      unmappableCount,
      successRate,
      breakdown
    }
  }, [customers.length, mappableCustomers.length, coloringMode, colorPalette])

  // Map center and zoom - show world view initially
  const mapCenter: [number, number] = [20, 0] // Center on world
  const mapZoom = 2

  useEffect(() => {
    setMapLoaded(true)
  }, [])

  // Handle back to customers
  const handleBackToCustomers = () => {
    navigate('/crm/customers')
  }

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isFullscreen])

  // Show loading state while customers are being loaded. Inlined inside the
  // card so it doesn't hijack the whole viewport height.
  if (loading) {
    return (
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ display: "flex", mr: 1 }}><Globe size={20} /></Box>
              Customer &amp; Prospect World Map
            </Box>
          }
          subheader="Loading customer locations…"
        />
        <CardContent>
          <Skeleton variant="rounded" sx={{ width: "100%", height: 720 }} />
        </CardContent>
      </Card>
    )
  }

  // Fullscreen mode rendering
  if (isFullscreen) {
    // Globe mode gets a deep-space radial backdrop; flat mode a plain white
    // canvas. These are map-presentation colors (data/chrome for the globe),
    // kept literal.
    const fullscreenBg =
      mapMode === 'globe'
        ? 'radial-gradient(circle at 50% 50%, #0a1628 0%, #020510 85%)'
        : '#ffffff'

    return (
      <Box sx={{ position: "fixed", inset: 0, zIndex: 50, background: fullscreenBg }}>
        {/* Top-right toolbar: single grouped pill */}
        <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 10, display: "flex", alignItems: "center", gap: 1, borderRadius: 999, border: 1, borderColor: (t) => alpha(t.palette.divider, 0.6), bgcolor: (t) => alpha(t.palette.background.paper, 0.9), p: 0.5, boxShadow: 3, backdropFilter: "blur(12px)" }}>
          {/* Mode toggle — segmented control */}
          <Box sx={{ display: "inline-flex", borderRadius: 999, bgcolor: "action.hover", p: 0.25 }}>
            <Button
              onClick={() => setMapMode('flat')}
              variant={mapMode === 'flat' ? 'contained' : 'text'}
              color={mapMode === 'flat' ? 'primary' : 'inherit'}
              size="small"
              startIcon={<MapIcon size={14} />}
              sx={{ height: 32, borderRadius: 999, px: 1.5, fontSize: "0.75rem", fontWeight: 500 }}
            >
              Flat
            </Button>
            <Button
              onClick={() => setMapMode('globe')}
              variant={mapMode === 'globe' ? 'contained' : 'text'}
              color={mapMode === 'globe' ? 'primary' : 'inherit'}
              size="small"
              startIcon={<Globe size={14} />}
              sx={{ height: 32, borderRadius: 999, px: 1.5, fontSize: "0.75rem", fontWeight: 500 }}
            >
              Globe
            </Button>
          </Box>

          <Box sx={{ height: 24, width: "1px", bgcolor: (t) => alpha(t.palette.divider, 0.7) }} aria-hidden="true" />

          <Button
            onClick={toggleFullscreen}
            variant="text"
            color="inherit"
            size="small"
            startIcon={<Minimize size={14} />}
            sx={{ height: 32, borderRadius: 999, px: 1.5, fontSize: "0.75rem" }}
          >
            Exit Fullscreen
          </Button>
          <BackButton
            label="Back to Customers"
            onClick={handleBackToCustomers}
            sx={{ borderRadius: 999 }}
          />
        </Box>

        {/* Legend — bottom-left, out of the toolbar's way */}
        {Object.keys(stats.breakdown).length > 0 && (
          <Box sx={{ position: "absolute", bottom: 24, left: 24, zIndex: 10, maxWidth: 320, borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.divider, 0.6), bgcolor: (t) => alpha(t.palette.background.paper, 0.9), p: 1.5, boxShadow: 3, backdropFilter: "blur(12px)" }}>
            <Box sx={{ display: "flex", maxHeight: 192, flexDirection: "column", gap: 0.75, overflowY: "auto", fontSize: "0.75rem" }}>
              {Object.entries(stats.breakdown).map(([key, count]) => {
                const color = colorPalette[key] || '#999999'
                return (
                  <Box key={key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
                    <Box sx={{ display: "flex", minWidth: 0, flex: 1, alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{ height: 8, width: 8, flexShrink: 0, borderRadius: "50%" }}
                        style={{ backgroundColor: color }}
                      />
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={key}>
                        {key}
                      </Box>
                    </Box>
                    <Box component="span" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums", color: "text.secondary" }}>
                      {count}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>
        )}

        {/* Fullscreen Map — inset a bit so the globe/map breathes */}
        <Box sx={{ height: "100%", width: "100%", p: { xs: 2, md: 3 } }}>
          {!mapLoaded ? (
            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
              <Box sx={{ textAlign: "center" }}>
                <Box sx={{ mx: "auto", mb: 2, height: 40, width: 40, borderRadius: "50%", borderBottom: "2px solid", borderColor: "primary.main", animation: "cwm-spin 1s linear infinite", "@keyframes cwm-spin": { to: { transform: "rotate(360deg)" } } }} />
                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Preparing map…</Typography>
              </Box>
            </Box>
          ) : mapMode === 'globe' ? (
            <CustomerWorldGlobe
              customers={mappableCustomers}
              onCustomerClick={onCustomerClick}
              getMarkerColor={getMarkerColor}
              height="100%"
            />
          ) : (
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%', zIndex: 0 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              <ZoomReporter onZoom={setZoomLevel} />

              {mappableCustomers.map((customer) => (
                <Marker
                  key={customer.id}
                  position={[customer.latitude!, customer.longitude!]}
                  icon={getMarkerIcon(customer)}
                >
                  <Popup>
                    <Box sx={{ p: 1, minWidth: 250 }}>
                      <Box sx={{ fontWeight: 600, fontSize: "1rem", mb: 1 }}>
                        {customer.company_name || 'No Company'}
                      </Box>
                      <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 1 }}>
                        {customer.full_name}
                      </Box>

                        <Box sx={{ fontSize: "0.75rem", color: "text.disabled", mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <Box
                              component="span"
                              sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%" }}
                              style={{ backgroundColor: (customer as any).type?.toLowerCase() === 'lead' ? '#3b82f6' : '#ef4444' }}
                            />
                            <Box component="span">{(customer as any).type?.toLowerCase() === 'lead' ? 'Prospect' : 'Customer'}</Box>
                          </Box>
                          {coloringMode !== 'type' && (
                            <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                              <Box><strong>{coloringMode.charAt(0).toUpperCase() + coloringMode.slice(1)}:</strong> {getCustomerDisplayValue(customer)}</Box>
                            </Box>
                          )}
                        </Box>

                      {customer.email && (
                        <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
                          <strong>Email:</strong> {customer.email}
                        </Box>
                      )}

                      {customer.phone && (
                        <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 1 }}>
                          <strong>Phone:</strong> {customer.phone}
                        </Box>
                      )}

                      <PinProvenance customer={customer} />

                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                        <Chip size="small" color="default" label={customer.status} />

                        {onCustomerClick && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => onCustomerClick(customer)}
                            sx={{ ml: 1 }}
                          >
                            View Details
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Map */}
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Box sx={{ display: "flex", mr: 1 }}><Globe size={20} /></Box>
                  Customer &amp; Prospect World Map
                </Box>
                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", fontWeight: 400, mt: 0.5 }}>
                  {hasProductFilter
                    ? 'Each marker is colored by product feature and version.'
                    : 'Customers are shown in red, prospects in blue.'}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                {/* Flat / Globe mode toggle — segmented control */}
                <Box sx={{ display: "inline-flex", borderRadius: 1, bgcolor: "action.hover", p: 0.25 }}>
                  <Button
                    onClick={() => setMapMode('flat')}
                    variant={mapMode === 'flat' ? 'contained' : 'text'}
                    color={mapMode === 'flat' ? 'primary' : 'inherit'}
                    size="small"
                    startIcon={<MapIcon size={14} />}
                    sx={{ height: 32, borderRadius: 1, px: 1.5, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Flat
                  </Button>
                  <Button
                    onClick={() => setMapMode('globe')}
                    variant={mapMode === 'globe' ? 'contained' : 'text'}
                    color={mapMode === 'globe' ? 'primary' : 'inherit'}
                    size="small"
                    startIcon={<Globe size={14} />}
                    sx={{ height: 32, borderRadius: 1, px: 1.5, fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Globe
                  </Button>
                </Box>
                <Button onClick={toggleFullscreen} variant="outlined" color="inherit" size="small" startIcon={<Maximize size={14} />} sx={{ height: 32 }}>
                  Fullscreen
                </Button>
                <BackButton onClick={handleBackToCustomers} />
              </Box>
            </Box>
          }
        />
        <CardContent>
          {/* Filters Section */}
          {filters && onFilterChange && productHierarchy && (
            <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)", xl: "repeat(5, 1fr)" }, gap: 1.5 }}>
                {/* Customer Type Filter */}
                <Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={filters.customerType}
                    onChange={(e) => onFilterChange('customerType', e.target.value as 'all' | 'leads' | 'customers')}
                  >
                    <MenuItem value="all">All Types ({(allCustomers || customers).length})</MenuItem>
                    <MenuItem value="leads">Prospects Only ({(allCustomers || customers).filter(c => (c as any).type?.toLowerCase() === 'lead').length})</MenuItem>
                    <MenuItem value="customers">Customers Only ({(allCustomers || customers).filter(c => (c as any).type?.toLowerCase() !== 'lead').length})</MenuItem>
                  </TextField>
                </Box>

                {/* Product Filter */}
                <Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={filters.productId?.toString() || "all"}
                    onChange={(e) => onFilterChange('productId', e.target.value === "all" ? null : parseInt(e.target.value))}
                  >
                    <MenuItem value="all">All Products ({(allCustomers || customers).length})</MenuItem>
                    {productHierarchy.products.map(product => {
                      const productCount = (allCustomers || customers).filter(c => (c as any).product?.id === product.id).length
                      return (
                        <MenuItem key={product.id} value={product.id.toString()}>
                          {product.name} {product.code && `(${product.code})`} ({productCount})
                        </MenuItem>
                      )
                    })}
                  </TextField>
                </Box>

                {/* Product Feature Filter */}
                <Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={filters.productFeatureId?.toString() || "all"}
                    onChange={(e) => onFilterChange('productFeatureId', e.target.value === "all" ? null : parseInt(e.target.value))}
                    disabled={!filters.productId || (productHierarchy.product_features[filters.productId] || []).length === 0}
                  >
                    <MenuItem value="all">All Features ({(allCustomers || customers).filter(c => (c as any).product?.id === filters.productId).length})</MenuItem>
                    {(filters.productId != null ? (productHierarchy.product_features[filters.productId] || []) : []).map((feature: any) => {
                      const featureCount = (allCustomers || customers).filter(c => (c as any).product_feature?.id === feature.id).length
                      return (
                        <MenuItem key={feature.id} value={feature.id.toString()}>
                          {feature.name} {feature.code && `(${feature.code})`} ({featureCount})
                        </MenuItem>
                      )
                    })}
                  </TextField>
                </Box>

                {/* Reset Button */}
                {getActiveFilterCount && onResetFilters && getActiveFilterCount() > 0 && (
                  <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={onResetFilters}
                      startIcon={<X size={12} />}
                      sx={{ height: 32, fontSize: "0.75rem", width: "100%" }}
                    >
                      Reset ({getActiveFilterCount()})
                    </Button>
                  </Box>
                )}
              </Box>

            </Box>
          )}

          {!mapLoaded ? (
            <Skeleton variant="rounded" sx={{ width: "100%", height: 720 }} />
          ) : mapMode === 'globe' ? (
            <Box sx={{ position: "relative", width: "100%", height: 720, overflow: "hidden", borderRadius: 1, border: 1, borderColor: "divider" }}>
              <CustomerWorldGlobe
                customers={mappableCustomers}
                onCustomerClick={onCustomerClick}
                getMarkerColor={getMarkerColor}
                height="100%"
              />
            </Box>
          ) : (
            <Box sx={{ position: "relative", width: "100%", height: 720, overflow: "hidden", borderRadius: 1, border: 1, borderColor: "divider" }}>
              <MapContainer
                center={mapCenter}
                zoom={mapZoom}
                style={{ height: '100%', width: '100%', zIndex: 0 }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                <ZoomReporter onZoom={setZoomLevel} />

                              {mappableCustomers.map((customer) => (
                <Marker
                  key={customer.id}
                  position={[customer.latitude!, customer.longitude!]}
                  icon={getMarkerIcon(customer)}
                >
                    <Popup>
                      <Box sx={{ p: 1, minWidth: 250 }}>
                        <Box sx={{ fontWeight: 600, fontSize: "1rem", mb: 1 }}>
                          {customer.company_name || 'No Company'}
                        </Box>
                        <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 1 }}>
                          {customer.full_name}
                        </Box>

                        <Box sx={{ fontSize: "0.75rem", color: "text.disabled", mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                            <Box
                              component="span"
                              sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%" }}
                              style={{ backgroundColor: (customer as any).type?.toLowerCase() === 'lead' ? '#3b82f6' : '#ef4444' }}
                            />
                            <Box component="span">{(customer as any).type?.toLowerCase() === 'lead' ? 'Prospect' : 'Customer'}</Box>
                          </Box>
                          {coloringMode !== 'type' && (
                            <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                              <Box><strong>{coloringMode.charAt(0).toUpperCase() + coloringMode.slice(1)}:</strong> {getCustomerDisplayValue(customer)}</Box>
                            </Box>
                          )}
                        </Box>

                        {customer.email && (
                          <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 0.5 }}>
                            <strong>Email:</strong> {customer.email}
                          </Box>
                        )}

                        {customer.phone && (
                          <Box sx={{ fontSize: "0.875rem", color: "text.secondary", mb: 1 }}>
                            <strong>Phone:</strong> {customer.phone}
                          </Box>
                        )}

                        <PinProvenance customer={customer} />

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                          <Chip size="small" color="default" label={customer.status} />

                          {onCustomerClick && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => onCustomerClick(customer)}
                              sx={{ ml: 1 }}
                            >
                              View Details
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>

            </Box>
          )}
        </CardContent>
      </Card>

      {/* Customers without addresses */}
      {stats.unmappableCount > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.75rem", color: "text.secondary", px: 0.5 }}>
          <Box component="span" sx={{ display: "flex", color: "warning.main", flexShrink: 0 }}><AlertCircle size={14} /></Box>
          <Box component="span">
            {stats.unmappableCount} of {stats.totalCustomers} {stats.unmappableCount === 1 ? 'record' : 'records'} not shown — missing address data
          </Box>
        </Box>
      )}
    </Box>
  )
}
